// Multi-country fiscal code (national identification number) validators.
// Implements format check + official checksum for the 18 supported countries.
//
// Returns: { valid: boolean, error?: string, label: string, example: string }

import { validateCodiceFiscale as _validateITCF, validateVatNumber as _validateITVAT } from "./fiscalCode";
import { normalizeCountryCode } from "../constants/countries";

// Country → metadata (label of the local document + example shown to the user
// + i18n translations for the 6 native UI languages — req. Fabrizio 10/06/2026:
// la label del documento deve tradursi al 100% nella lingua UI scelta dal cliente,
// preservando però il riferimento al PAESE del documento — un cliente italiano
// che apre l'UI in inglese vede "Italian Fiscal Code", non "National Insurance No.").
// Non-native UI languages (NL/PT/SV/NO/FI/DK/CZ/EL/CH/BE/GBZ/IRL) fall back to UK English.
export const FISCAL_CODE_INFO = {
  IT:  { label: "Codice Fiscale",         example: "RSSMRA85M01H501Z",
         i18nLabels: { IT: "Codice Fiscale", UK: "Italian Fiscal Code",
                        FR: "Code Fiscal Italien", ES: "Código Fiscal Italiano",
                        DE: "Italienische Steuernummer", PL: "Włoski kod podatkowy" } },
  FR:  { label: "Numéro NIR",             example: "1850578006048 22",
         i18nLabels: { IT: "Numero NIR francese", UK: "French Social Security No. (NIR)",
                        FR: "Numéro NIR", ES: "Número NIR francés",
                        DE: "Französische Sozialversicherungsnummer (NIR)",
                        PL: "Francuski numer NIR" } },
  ES:  { label: "NIF / NIE",              example: "12345678Z",
         i18nLabels: { IT: "NIF / NIE spagnolo", UK: "Spanish NIF / NIE",
                        FR: "NIF / NIE espagnol", ES: "NIF / NIE",
                        DE: "Spanische NIF / NIE", PL: "Hiszpański NIF / NIE" } },
  DE:  { label: "Steuer-ID",              example: "47036892816",
         i18nLabels: { IT: "Codice Fiscale tedesco (Steuer-ID)", UK: "German Tax ID (Steuer-ID)",
                        FR: "Numéro Fiscal Allemand (Steuer-ID)", ES: "Identificación Fiscal Alemana (Steuer-ID)",
                        DE: "Steuer-ID", PL: "Niemiecki numer podatkowy (Steuer-ID)" } },
  UK:  { label: "National Insurance No.", example: "AB123456C",
         i18nLabels: { IT: "Codice NIN britannico", UK: "National Insurance No.",
                        FR: "Numéro NIN britannique", ES: "Número NIN británico",
                        DE: "Britische Versicherungsnummer (NIN)",
                        PL: "Brytyjski numer NIN" } },
  PT:  { label: "NIF",                    example: "123456789",
         i18nLabels: { IT: "NIF portoghese", UK: "Portuguese NIF",
                        FR: "NIF portugais", ES: "NIF portugués",
                        DE: "Portugiesische NIF", PL: "Portugalski NIF" } },
  NL:  { label: "BSN",                    example: "111222333",
         i18nLabels: { IT: "BSN olandese", UK: "Dutch BSN",
                        FR: "BSN néerlandais", ES: "BSN holandés",
                        DE: "Niederländische BSN", PL: "Holenderski BSN" } },
  BE:  { label: "Numéro National",        example: "85.07.30-033 28",
         i18nLabels: { IT: "Numero Nazionale belga", UK: "Belgian National Number",
                        FR: "Numéro National", ES: "Número Nacional belga",
                        DE: "Belgische Nationalnummer", PL: "Belgijski numer narodowy" } },
  PL:  { label: "PESEL",                  example: "44051401358",
         i18nLabels: { IT: "PESEL polacco", UK: "Polish PESEL",
                        FR: "PESEL polonais", ES: "PESEL polaco",
                        DE: "Polnische PESEL-Nummer", PL: "PESEL" } },
  SE:  { label: "Personnummer",           example: "811228-9874",
         i18nLabels: { IT: "Personnummer svedese", UK: "Swedish Personnummer",
                        FR: "Personnummer suédois", ES: "Personnummer sueco",
                        DE: "Schwedische Personennummer", PL: "Szwedzki Personnummer" } },
  NO:  { label: "Fødselsnummer",          example: "29029900158",
         i18nLabels: { IT: "Fødselsnummer norvegese", UK: "Norwegian Fødselsnummer",
                        FR: "Fødselsnummer norvégien", ES: "Fødselsnummer noruego",
                        DE: "Norwegische Fødselsnummer", PL: "Norweski Fødselsnummer" } },
  FI:  { label: "Henkilötunnus",          example: "131052-308T",
         i18nLabels: { IT: "Henkilötunnus finlandese", UK: "Finnish Henkilötunnus",
                        FR: "Henkilötunnus finlandais", ES: "Henkilötunnus finlandés",
                        DE: "Finnische Henkilötunnus", PL: "Fińskie Henkilötunnus" } },
  DK:  { label: "CPR-nummer",             example: "010180-1234",
         i18nLabels: { IT: "CPR-nummer danese", UK: "Danish CPR Number",
                        FR: "Numéro CPR danois", ES: "Número CPR danés",
                        DE: "Dänische CPR-Nummer", PL: "Duński numer CPR" } },
  CZ:  { label: "Rodné číslo",            example: "780123/3540",
         i18nLabels: { IT: "Rodné číslo ceco", UK: "Czech Rodné Číslo",
                        FR: "Rodné číslo tchèque", ES: "Rodné Číslo checo",
                        DE: "Tschechische Rodné Číslo", PL: "Czeskie Rodné Číslo" } },
  GR:  { label: "AFM",                    example: "123456789",
         i18nLabels: { IT: "AFM greco", UK: "Greek AFM",
                        FR: "AFM grec", ES: "AFM griego",
                        DE: "Griechische AFM-Nummer", PL: "Greckie AFM" } },
  CH:  { label: "AHV-Nummer",             example: "756.1234.5678.97",
         i18nLabels: { IT: "AHV-Nummer svizzero", UK: "Swiss AHV-Nummer",
                        FR: "Numéro AVS suisse", ES: "AHV-Nummer suizo",
                        DE: "AHV-Nummer", PL: "Szwajcarski AHV-Nummer" } },
  GBZ: { label: "National Insurance No.", example: "AB123456C",
         i18nLabels: { IT: "Codice NIN di Gibilterra", UK: "National Insurance No.",
                        FR: "Numéro NIN de Gibraltar", ES: "Número NIN de Gibraltar",
                        DE: "Versicherungsnummer Gibraltar (NIN)", PL: "Numer NIN Gibraltaru" } },
  IRL: { label: "PPS Number",             example: "1234567TA",
         i18nLabels: { IT: "Numero PPS irlandese", UK: "Irish PPS Number",
                        FR: "Numéro PPS irlandais", ES: "Número PPS irlandés",
                        DE: "Irische PPS-Nummer", PL: "Irlandzki numer PPS" } },
};

const stripWS = (s) => String(s || "").replace(/[\s\-./]/g, "").toUpperCase();

// ------------ Helpers ------------
const onlyDigits = (s) => /^\d+$/.test(s);

const mod = (n, m) => ((n % m) + m) % m;

// ------------ Per-country validators ------------

const validateFR = (code) => {
  // NIR: 13 digits + 2-digit key (97 - (first13 mod 97)). Corsica uses 2A/2B
  // (replaced by 19/18 for checksum). Length 13 or 15.
  let c = code;
  if (c.length !== 15 && c.length !== 13) return { valid: false, error: "Il NIR francese deve essere di 13 o 15 cifre" };
  // Handle Corsica letters in positions 6-7
  const normalized = c.replace(/2A/g, "19").replace(/2B/g, "18");
  if (!/^\d{13}(\d{2})?$/.test(normalized)) return { valid: false, error: "Il NIR contiene caratteri non validi" };
  if (normalized.length === 13) return { valid: true };
  const body = BigInt(normalized.substring(0, 13));
  const key = parseInt(normalized.substring(13, 15), 10);
  const expected = 97 - Number(body % 97n);
  if (key !== expected) return { valid: false, error: "Chiave di controllo del NIR non valida" };
  return { valid: true };
};

const ES_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";
const validateES = (code) => {
  // NIF: 8 digits + 1 letter
  // NIE: starts with X|Y|Z (0|1|2) + 7 digits + 1 letter
  if (code.length !== 9) return { valid: false, error: "NIF/NIE deve essere di 9 caratteri (8 cifre + 1 lettera)" };
  let body, letter;
  if (/^[XYZ]/.test(code)) {
    const prefixMap = { X: "0", Y: "1", Z: "2" };
    body = prefixMap[code[0]] + code.substring(1, 8);
    letter = code[8];
  } else {
    body = code.substring(0, 8);
    letter = code[8];
  }
  if (!/^\d{8}$/.test(body)) return { valid: false, error: "Il NIF/NIE deve avere 8 cifre" };
  if (!/^[A-Z]$/.test(letter)) return { valid: false, error: "L'ultima posizione deve essere una lettera" };
  const expected = ES_LETTERS[parseInt(body, 10) % 23];
  if (expected !== letter) return { valid: false, error: "Lettera di controllo NIF/NIE non valida" };
  return { valid: true };
};

const validateDE = (code) => {
  // Steuer-ID: 11 digits. Exactly one digit must appear twice in positions
  // 1..10, all others appear once. Last digit is mod-11-based checksum.
  if (code.length !== 11 || !onlyDigits(code)) return { valid: false, error: "Lo Steuer-ID deve essere di 11 cifre" };
  const first10 = code.substring(0, 10);
  // Counts of digits in first10
  const counts = {};
  for (const c of first10) counts[c] = (counts[c] || 0) + 1;
  const repeated = Object.values(counts).filter((v) => v >= 2).length;
  if (repeated !== 1) return { valid: false, error: "Formato Steuer-ID non valido" };
  // Checksum (ISO 7064 MOD 11,10)
  let product = 10;
  for (let i = 0; i < 10; i++) {
    let sum = (parseInt(first10[i], 10) + product) % 10;
    if (sum === 0) sum = 10;
    product = (sum * 2) % 11;
  }
  const check = (11 - product) % 10;
  if (check !== parseInt(code[10], 10)) return { valid: false, error: "Checksum Steuer-ID non valido" };
  return { valid: true };
};

const UK_INVALID_PREFIX = new Set(["BG", "GB", "KN", "NK", "NT", "TN", "ZZ"]);
const UK_INVALID_FIRST = new Set(["D", "F", "I", "Q", "U", "V"]);
const UK_INVALID_SECOND = new Set(["D", "F", "I", "O", "Q", "U", "V"]);
const validateUK = (code) => {
  // NINO: 2 letters + 6 digits + 1 letter (A/B/C/D)
  if (code.length !== 9) return { valid: false, error: "Il National Insurance Number deve essere di 9 caratteri" };
  if (!/^[A-Z]{2}\d{6}[A-D]$/.test(code)) return { valid: false, error: "Formato National Insurance Number non valido" };
  const prefix = code.substring(0, 2);
  if (UK_INVALID_FIRST.has(prefix[0]) || UK_INVALID_SECOND.has(prefix[1]) || UK_INVALID_PREFIX.has(prefix)) {
    return { valid: false, error: "Prefisso NINO non valido" };
  }
  return { valid: true };
};

const validatePT = (code) => {
  // NIF: 9 digits. mod-11 with weights 9..2 on first 8.
  if (code.length !== 9 || !onlyDigits(code)) return { valid: false, error: "Il NIF portoghese deve essere di 9 cifre" };
  let sum = 0;
  for (let i = 0; i < 8; i++) sum += parseInt(code[i], 10) * (9 - i);
  let check = 11 - (sum % 11);
  if (check >= 10) check = 0;
  if (check !== parseInt(code[8], 10)) return { valid: false, error: "Checksum NIF portoghese non valido" };
  return { valid: true };
};

const validateNL = (code) => {
  // BSN: 8 or 9 digits, 11-test: sum(d_i * w_i) mod 11 == 0 where w = 9..2,-1
  if (!onlyDigits(code) || (code.length !== 8 && code.length !== 9)) return { valid: false, error: "Il BSN olandese deve essere di 8 o 9 cifre" };
  const padded = code.padStart(9, "0");
  const weights = [9, 8, 7, 6, 5, 4, 3, 2, -1];
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(padded[i], 10) * weights[i];
  if (sum % 11 !== 0) return { valid: false, error: "BSN non valido (test 11)" };
  return { valid: true };
};

const validateBE = (code) => {
  // Numéro National: 11 digits. For births >= 2000, prefix "2" before mod.
  if (code.length !== 11 || !onlyDigits(code)) return { valid: false, error: "Il Numéro National deve essere di 11 cifre" };
  const body = code.substring(0, 9);
  const check = parseInt(code.substring(9, 11), 10);
  // <2000
  if (97 - (BigInt(body) % 97n) === BigInt(check)) return { valid: true };
  // >=2000
  if (97 - (BigInt("2" + body) % 97n) === BigInt(check)) return { valid: true };
  return { valid: false, error: "Checksum Numéro National non valido" };
};

const PESEL_WEIGHTS = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
const validatePL = (code) => {
  // PESEL: 11 digits, checksum custom
  if (code.length !== 11 || !onlyDigits(code)) return { valid: false, error: "Il PESEL deve essere di 11 cifre" };
  let sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(code[i], 10) * PESEL_WEIGHTS[i];
  const check = (10 - (sum % 10)) % 10;
  if (check !== parseInt(code[10], 10)) return { valid: false, error: "Checksum PESEL non valido" };
  return { valid: true };
};

const luhn = (s) => {
  let sum = 0;
  let alt = false;
  for (let i = s.length - 1; i >= 0; i--) {
    let n = parseInt(s[i], 10);
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
};

const validateSE = (code) => {
  // Personnummer: 10 (YYMMDD-NNNN) or 12 (YYYYMMDDNNNN) digits. Luhn on last 10.
  if (!onlyDigits(code) || (code.length !== 10 && code.length !== 12)) return { valid: false, error: "Il Personnummer deve essere di 10 o 12 cifre" };
  const last10 = code.slice(-10);
  if (!luhn(last10)) return { valid: false, error: "Checksum Personnummer non valido" };
  return { valid: true };
};

const NO_W1 = [3, 7, 6, 1, 8, 9, 4, 5, 2];
const NO_W2 = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
const validateNO = (code) => {
  // Fødselsnummer: 11 digits, two control digits mod 11
  if (code.length !== 11 || !onlyDigits(code)) return { valid: false, error: "Il Fødselsnummer deve essere di 11 cifre" };
  let s1 = 0;
  for (let i = 0; i < 9; i++) s1 += parseInt(code[i], 10) * NO_W1[i];
  let k1 = 11 - (s1 % 11);
  if (k1 === 11) k1 = 0;
  if (k1 === 10) return { valid: false, error: "Fødselsnummer non valido" };
  if (k1 !== parseInt(code[9], 10)) return { valid: false, error: "Prima cifra di controllo non valida" };
  let s2 = 0;
  for (let i = 0; i < 10; i++) s2 += parseInt(code[i], 10) * NO_W2[i];
  let k2 = 11 - (s2 % 11);
  if (k2 === 11) k2 = 0;
  if (k2 === 10) return { valid: false, error: "Fødselsnummer non valido" };
  if (k2 !== parseInt(code[10], 10)) return { valid: false, error: "Seconda cifra di controllo non valida" };
  return { valid: true };
};

const FI_CHECK = "0123456789ABCDEFHJKLMNPRSTUVWXY";
const validateFI = (code) => {
  // HETU: DDMMYY + sep + ID(3) + check(1). Sep depends on century.
  // After our stripWS the separators (- + A) are removed. Recover: length 10
  // is the cleaned form (6 + 3 + 1). We accept that.
  if (code.length !== 10) return { valid: false, error: "Il codice HETU deve essere di 10 caratteri" };
  const datePart = code.substring(0, 6);
  const idPart = code.substring(6, 9);
  const checkChar = code[9];
  if (!onlyDigits(datePart) || !onlyDigits(idPart)) return { valid: false, error: "Formato HETU non valido" };
  const num = parseInt(datePart + idPart, 10);
  const expected = FI_CHECK[num % 31];
  if (expected !== checkChar) return { valid: false, error: "Carattere di controllo HETU non valido" };
  return { valid: true };
};

const validateDK = (code) => {
  // CPR: 10 digits. The mod-11 check has been officially abandoned (2007), so
  // we only verify format + plausibility of the date.
  if (code.length !== 10 || !onlyDigits(code)) return { valid: false, error: "Il CPR-nummer deve essere di 10 cifre" };
  const dd = parseInt(code.substring(0, 2), 10);
  const mm = parseInt(code.substring(2, 4), 10);
  if (dd < 1 || dd > 31 || mm < 1 || mm > 12) return { valid: false, error: "Data di nascita CPR non valida" };
  return { valid: true };
};

const validateCZ = (code) => {
  // Rodné číslo: 9 (pre-1954) or 10 digits, divisible by 11 (with exception
  // for 10-digit form where check digit is 0 if reminder is 10).
  if (!onlyDigits(code) || (code.length !== 9 && code.length !== 10)) return { valid: false, error: "Il Rodné číslo deve essere di 9 o 10 cifre" };
  if (code.length === 9) return { valid: true }; // pre-1954, only date validation
  const n = BigInt(code);
  if (n % 11n !== 0n) {
    // Allowed exception: 10-digit RC where reminder == 10 keeps "0" as last digit.
    const reminder = Number(BigInt(code.substring(0, 9)) % 11n);
    if (reminder === 10 && code[9] === "0") return { valid: true };
    return { valid: false, error: "Rodné číslo non valido" };
  }
  return { valid: true };
};

const validateGR = (code) => {
  // AFM: 9 digits, checksum: sum(d_i * 2^(9-i)) % 11 mod 10 == d_9
  if (code.length !== 9 || !onlyDigits(code)) return { valid: false, error: "L'AFM deve essere di 9 cifre" };
  if (/^0{9}$/.test(code)) return { valid: false, error: "AFM non valido" };
  let sum = 0;
  for (let i = 0; i < 8; i++) sum += parseInt(code[i], 10) * (1 << (8 - i));
  const check = (sum % 11) % 10;
  if (check !== parseInt(code[8], 10)) return { valid: false, error: "Checksum AFM non valido" };
  return { valid: true };
};

const validateCH = (code) => {
  // AHV-Nummer (Swiss): 13 digits starting with 756, EAN-13 checksum on last.
  if (code.length !== 13 || !onlyDigits(code) || !code.startsWith("756")) return { valid: false, error: "AHV-Nummer deve iniziare con 756 e avere 13 cifre" };
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += parseInt(code[i], 10) * (i % 2 === 0 ? 1 : 3);
  const check = (10 - (sum % 10)) % 10;
  if (check !== parseInt(code[12], 10)) return { valid: false, error: "Checksum AHV-Nummer non valido" };
  return { valid: true };
};

const IRL_LETTERS = "WABCDEFGHIJKLMNOPQRSTUV";
const validateIRL = (code) => {
  // PPS Number: 7 digits + 1 letter (mod 23 → IRL_LETTERS) + optional letter
  // (A, H, T, or W for additional codes). Format: 7 digits + 1-2 letters.
  if (code.length !== 8 && code.length !== 9) return { valid: false, error: "Il PPS deve essere di 8 o 9 caratteri" };
  if (!/^\d{7}[A-W]([A-Z])?$/.test(code)) return { valid: false, error: "Formato PPS non valido" };
  const digits = code.substring(0, 7);
  const check = code[7];
  const ninthChar = code.length === 9 ? code[8] : "";
  // Weights: position 1..7 -> 8..2
  let sum = 0;
  for (let i = 0; i < 7; i++) sum += parseInt(digits[i], 10) * (8 - i);
  // If a 9th letter is present, add its alpha position * 9 to the sum
  if (ninthChar) {
    const alphaPos = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".indexOf(ninthChar);
    if (alphaPos === -1) return { valid: false, error: "Carattere finale PPS non valido" };
    sum += (alphaPos + 1) * 9;
  }
  const expected = IRL_LETTERS[sum % 23];
  if (expected !== check) return { valid: false, error: "Carattere di controllo PPS non valido" };
  return { valid: true };
};

// ------------ Dispatcher ------------

const VALIDATORS = {
  IT:  (c) => _validateITCF(c),
  FR:  validateFR,
  ES:  validateES,
  DE:  validateDE,
  UK:  validateUK,
  PT:  validatePT,
  NL:  validateNL,
  BE:  validateBE,
  PL:  validatePL,
  SE:  validateSE,
  NO:  validateNO,
  FI:  validateFI,
  DK:  validateDK,
  CZ:  validateCZ,
  GR:  validateGR,
  CH:  validateCH,
  GBZ: validateUK,
  IRL: validateIRL,
};

export const getFiscalCodeInfo = (country, uiLang) => {
  // Fabrizio bug 06/07/2026 pt.3 — Il DB salva ``GB``/``IE``/``GI`` (ISO)
  // ma ``FISCAL_CODE_INFO`` usa ``UK``/``IRL``/``GBZ`` (legacy).
  // Senza normalizzazione, un cliente UK vede il fallback Italia
  // ("Codice Fiscale · RSSMRA85M01H501Z") invece di
  // "National Insurance No. · AB123456C".
  const normCountry = normalizeCountryCode(country);
  const base = FISCAL_CODE_INFO[normCountry] || FISCAL_CODE_INFO.IT;
  // Resolve the UI-language label when uiLang is provided AND a
  // translation exists; otherwise fall back to the document-country
  // default. Non-native UI languages map to UK English via the wallet
  // language policy. CH→DE, BE→FR (sub-locales of the native packs).
  if (uiLang) {
    const _MAP = { CH: 'DE', BE: 'FR', GBZ: 'UK', IRL: 'UK' };
    const normUiLang = normalizeCountryCode(uiLang).toUpperCase();
    const code = (_MAP[normUiLang] || normUiLang);
    const NATIVES = ['IT', 'UK', 'FR', 'ES', 'DE', 'PL'];
    const resolved = NATIVES.includes(code) ? code : 'UK';
    const i18nLabels = base.i18nLabels || {};
    const localizedLabel = i18nLabels[resolved] || base.label;
    return { ...base, label: localizedLabel };
  }
  return base;
};

export const validateFiscalCode = (code, country) => {
  const normCountry = normalizeCountryCode(country);
  const info = getFiscalCodeInfo(normCountry);
  if (!code) {
    return { valid: false, error: `${info.label} obbligatorio`, label: info.label, example: info.example };
  }
  // IT validator keeps its raw input handling (uppercases + strips spaces).
  const validator = VALIDATORS[normCountry];
  if (!validator) {
    return { valid: true, label: info.label, example: info.example };
  }
  const normalized = normCountry === "IT" ? code : stripWS(code);
  const result = validator(normCountry === "IT" ? code : normalized);
  return { ...result, label: info.label, example: info.example };
};

// ---------------------- VAT (EU) ----------------------
//
// Format + checksum for EU country VAT numbers. Optional country prefix is
// stripped. We currently fully validate IT (with checksum); other countries
// use a length+regex check that catches typos but not all invalid combos.
// This is a deliberate scope choice — the deeper "registered & active"
// validation lives in phase 2 (VIES).

const VAT_RULES = {
  IT: { prefix: "IT", regex: /^\d{11}$/,                  example: "IT01234567890",   label: "Partita IVA italiana",
        i18nLabels: { IT: "Partita IVA italiana", UK: "Italian VAT Number",
                       FR: "Numéro TVA italien", ES: "NIF-IVA italiano",
                       DE: "Italienische Umsatzsteuer-Nr.", PL: "Włoski numer VAT (P.IVA)" } },
  FR: { prefix: "FR", regex: /^[A-HJ-NP-Z0-9]{2}\d{9}$/,   example: "FR12345678901",    label: "Numéro TVA francese",
        i18nLabels: { IT: "Numero TVA francese", UK: "French VAT Number",
                       FR: "Numéro TVA", ES: "Número TVA francés",
                       DE: "Französische TVA-Nr.", PL: "Francuski numer TVA" } },
  ES: { prefix: "ES", regex: /^[A-Z0-9]\d{7}[A-Z0-9]$/,    example: "ESX12345678",      label: "NIF-IVA spagnolo",
        i18nLabels: { IT: "NIF-IVA spagnolo", UK: "Spanish VAT Number (NIF-IVA)",
                       FR: "Numéro NIF-IVA espagnol", ES: "NIF-IVA",
                       DE: "Spanische NIF-IVA", PL: "Hiszpański NIF-IVA" } },
  DE: { prefix: "DE", regex: /^\d{9}$/,                    example: "DE123456789",      label: "Umsatzsteuer-ID tedesco",
        i18nLabels: { IT: "Umsatzsteuer-ID tedesco", UK: "German VAT ID (Umsatzsteuer-ID)",
                       FR: "Numéro TVA allemand (Umsatzsteuer-ID)", ES: "ID-IVA alemán (Umsatzsteuer-ID)",
                       DE: "Umsatzsteuer-ID", PL: "Niemiecki numer VAT (Umsatzsteuer-ID)" } },
  UK: { prefix: "GB", regex: /^(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/, example: "GB123456789", label: "UK VAT Number",
        i18nLabels: { IT: "Partita IVA britannica", UK: "UK VAT Number",
                       FR: "Numéro TVA britannique", ES: "Número VAT británico",
                       DE: "Britische Umsatzsteuer-Nr.", PL: "Brytyjski numer VAT" } },
  PT: { prefix: "PT", regex: /^\d{9}$/,                    example: "PT123456789",      label: "NIF portoghese",
        i18nLabels: { IT: "NIF portoghese", UK: "Portuguese NIF (VAT)",
                       FR: "NIF portugais (TVA)", ES: "NIF portugués (IVA)",
                       DE: "Portugiesische NIF (USt)", PL: "Portugalski NIF (VAT)" } },
  NL: { prefix: "NL", regex: /^\d{9}B\d{2}$/,              example: "NL123456789B01",   label: "BTW olandese",
        i18nLabels: { IT: "BTW olandese", UK: "Dutch BTW",
                       FR: "BTW néerlandais", ES: "BTW holandés",
                       DE: "Niederländische BTW", PL: "Holenderski BTW" } },
  BE: { prefix: "BE", regex: /^0?\d{9}$/,                  example: "BE0123456789",     label: "Numéro TVA belga",
        i18nLabels: { IT: "Numero TVA belga", UK: "Belgian VAT Number",
                       FR: "Numéro TVA belge", ES: "Número TVA belga",
                       DE: "Belgische USt-Nr.", PL: "Belgijski numer VAT" } },
  PL: { prefix: "PL", regex: /^\d{10}$/,                   example: "PL1234567890",     label: "NIP polacco",
        i18nLabels: { IT: "NIP polacco", UK: "Polish NIP (VAT)",
                       FR: "NIP polonais (TVA)", ES: "NIP polaco (IVA)",
                       DE: "Polnische NIP-Nummer (USt)", PL: "NIP" } },
  SE: { prefix: "SE", regex: /^\d{12}$/,                   example: "SE123456789012",   label: "Momsnummer svedese",
        i18nLabels: { IT: "Momsnummer svedese", UK: "Swedish Momsnummer (VAT)",
                       FR: "Momsnummer suédois (TVA)", ES: "Momsnummer sueco (IVA)",
                       DE: "Schwedische Momsnummer", PL: "Szwedzki Momsnummer (VAT)" } },
  NO: { prefix: "NO", regex: /^\d{9}(MVA)?$/,              example: "NO123456789MVA",   label: "MVA-nummer norvegese",
        i18nLabels: { IT: "MVA-nummer norvegese", UK: "Norwegian MVA Number",
                       FR: "Numéro MVA norvégien", ES: "Número MVA noruego",
                       DE: "Norwegische MVA-Nummer", PL: "Norweski numer MVA" } },
  FI: { prefix: "FI", regex: /^\d{8}$/,                    example: "FI12345678",       label: "ALV-numero finlandese",
        i18nLabels: { IT: "ALV-numero finlandese", UK: "Finnish ALV Number",
                       FR: "Numéro ALV finlandais", ES: "Número ALV finlandés",
                       DE: "Finnische ALV-Nummer", PL: "Fiński numer ALV" } },
  DK: { prefix: "DK", regex: /^\d{8}$/,                    example: "DK12345678",       label: "CVR-nummer danese",
        i18nLabels: { IT: "CVR-nummer danese", UK: "Danish CVR Number",
                       FR: "Numéro CVR danois", ES: "Número CVR danés",
                       DE: "Dänische CVR-Nummer", PL: "Duński numer CVR" } },
  CZ: { prefix: "CZ", regex: /^\d{8,10}$/,                 example: "CZ12345678",       label: "DIČ ceco",
        i18nLabels: { IT: "DIČ ceco", UK: "Czech DIČ (VAT)",
                       FR: "DIČ tchèque (TVA)", ES: "DIČ checo (IVA)",
                       DE: "Tschechische DIČ", PL: "Czeskie DIČ (VAT)" } },
  GR: { prefix: "EL", regex: /^\d{9}$/,                    example: "EL123456789",      label: "AFM greco",
        i18nLabels: { IT: "AFM greco (P.IVA)", UK: "Greek AFM (VAT)",
                       FR: "AFM grec (TVA)", ES: "AFM griego (IVA)",
                       DE: "Griechische AFM-USt-Nr.", PL: "Greckie AFM (VAT)" } },
  CH: { prefix: "CHE", regex: /^\d{9}(MWST|TVA|IVA)?$/,    example: "CHE123456789MWST", label: "Numero IVA svizzero",
        i18nLabels: { IT: "Numero IVA svizzero", UK: "Swiss VAT Number",
                       FR: "Numéro TVA suisse", ES: "Número IVA suizo",
                       DE: "Schweizer MWST-Nr.", PL: "Szwajcarski numer VAT" } },
  GBZ: { prefix: "GI", regex: /^\d{6}$/,                   example: "GI123456",         label: "Gibraltar VAT",
        i18nLabels: { IT: "P.IVA di Gibilterra", UK: "Gibraltar VAT",
                       FR: "TVA de Gibraltar", ES: "IVA de Gibraltar",
                       DE: "Gibraltar MWST", PL: "VAT Gibraltaru" } },
  IRL: { prefix: "IE", regex: /^[\dA-Z+\-*]{8,9}[A-Z]?$/,  example: "IE1234567T",       label: "VAT IE irlandese",
        i18nLabels: { IT: "P.IVA irlandese", UK: "Irish VAT Number",
                       FR: "Numéro TVA irlandais", ES: "Número IVA irlandés",
                       DE: "Irische USt-Nr.", PL: "Irlandzki numer VAT" } },
};

export const getVatInfo = (country, uiLang) => {
  // Fabrizio bug 06/07/2026 pt.3 — Normalizza ISO→legacy come per
  // getFiscalCodeInfo.
  const normCountry = normalizeCountryCode(country);
  const r = VAT_RULES[normCountry] || VAT_RULES.IT;
  let label = r.label;
  if (uiLang) {
    const _MAP = { CH: 'DE', BE: 'FR', GBZ: 'UK', IRL: 'UK' };
    const normUiLang = normalizeCountryCode(uiLang).toUpperCase();
    const code = (_MAP[normUiLang] || normUiLang);
    const NATIVES = ['IT', 'UK', 'FR', 'ES', 'DE', 'PL'];
    const resolved = NATIVES.includes(code) ? code : 'UK';
    label = (r.i18nLabels && r.i18nLabels[resolved]) || r.label;
  }
  return { example: r.example, label, prefix: r.prefix };
};

export const validateVat = (vat, country) => {
  const normCountry = normalizeCountryCode(country);
  const info = getVatInfo(normCountry);
  if (!vat) return { valid: true, label: info.label, example: info.example }; // optional
  const rule = VAT_RULES[normCountry] || VAT_RULES.IT;
  const raw = stripWS(vat);
  // Strip the country prefix if present
  const body = raw.startsWith(rule.prefix) ? raw.slice(rule.prefix.length) : raw;
  if (!rule.regex.test(body)) {
    return { valid: false, error: `Formato ${info.label} non valido`, label: info.label, example: info.example };
  }
  // Country-specific deep checksum (only IT for now — VIES handles the rest)
  if (normCountry === "IT") {
    const result = _validateITVAT(body);
    return { ...result, label: info.label, example: info.example };
  }
  return { valid: true, label: info.label, example: info.example };
};
